import React, { useState, useRef, useEffect } from 'react';

const VideoBackground = ({ videoSrc, posterSrc, className = '', children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsLoaded(true);
      // Ensure video plays on load
      video
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(err => {
          // console.log('Video autoplay failed:', err); // Removed debug log
        });
    };

    const handleError = e => {
      // console.error('Video failed to load:', e); // Removed debug log
      setHasError(true);
    };

    const handleCanPlay = () => {
      // console.log('Video can play'); // Removed debug log
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    // Removed debug log for video source

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoSrc]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Video Background */}
      {videoSrc && !hasError && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          autoPlay
          muted
          loop
          playsInline
          poster={posterSrc}
          preload="auto"
        >
          <source src={videoSrc} type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
      )}

      {/* Fallback Background (shows while loading or if video fails) */}
      {(!isLoaded || hasError) && posterSrc && (
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${posterSrc})` }}
        />
      )}

      {/* Static Gradient Background (fallback if no video/poster) */}
      {!posterSrc && (
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      )}

      {/* Overlay Gradients - Always Present */}
      <div className="absolute inset-0">
        {/* Primary overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/70" />

        {/* Secondary overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

        {/* Subtle color overlay to match ChillFree branding */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 via-transparent to-black/40" />
      </div>

      {/* Floating Elements - Animated Background Decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-32 h-44 bg-teal-500/10 rounded-lg rotate-12 animate-pulse" />
        <div className="absolute top-40 right-32 w-28 h-36 bg-orange-500/10 rounded-lg -rotate-6 animate-pulse delay-1000" />
        <div className="absolute bottom-32 left-1/3 w-24 h-32 bg-blue-500/10 rounded-lg rotate-6 animate-pulse delay-2000" />
        <div className="absolute bottom-20 right-20 w-30 h-40 bg-purple-500/10 rounded-lg -rotate-12 animate-pulse delay-3000" />
      </div>

      {/* Content Layer - Always on top */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default VideoBackground;
